import {
  RUBRIC_BAND_OPTIONS,
  classifyRubricBand,
  findScreenerTable,
  parseScreenerRows,
  type RubricBandId,
} from "./table";
import { syncResetButtons } from "./filterReset";

const FILTER_KEY = "sfe-rubric-band-filter";
const HOST_ID = "sfe-rubric-filter-host";
const ALL_IDS = RUBRIC_BAND_OPTIONS.map((o) => o.id);
const NATIVE_RUBRIC_SELECT_IDS = ["wheelPutRubricFilter", "wheelCallRubricFilter"] as const;

function loadSelected(): Set<RubricBandId> {
  try {
    const raw = localStorage.getItem(FILTER_KEY);
    if (!raw) return new Set(ALL_IDS);
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed) || parsed.length === 0) return new Set(ALL_IDS);
    const valid = parsed.filter((id): id is RubricBandId =>
      ALL_IDS.includes(id as RubricBandId),
    );
    return valid.length ? new Set(valid) : new Set(ALL_IDS);
  } catch {
    return new Set(ALL_IDS);
  }
}

function saveSelected(selected: Set<RubricBandId>): void {
  localStorage.setItem(FILTER_KEY, JSON.stringify([...selected]));
}

function isRubricBandLabel(el: Element): boolean {
  return /^rubric\s*band$/i.test((el.textContent ?? "").trim());
}

/** Locate the native Rubric Band filter field via its known select ids
 * (wheelPutRubricFilter / wheelCallRubricFilter). Text-only search inside the
 * table section wrongly matched column headers and mounted the control below
 * the screener summary. */
function findNativeRubricField(): HTMLElement | null {
  for (const id of NATIVE_RUBRIC_SELECT_IDS) {
    const sel = document.getElementById(id);
    if (!(sel instanceof HTMLElement) || !sel.isConnected) continue;

    let el: HTMLElement | null = sel.parentElement;
    while (el && el !== document.body) {
      for (const child of el.children) {
        if (isRubricBandLabel(child)) return el;
      }
      el = el.parentElement;
    }
  }
  return null;
}

function findFilterInsertPoint(
  _table: HTMLTableElement,
): { anchor: HTMLElement; replaceNativeRubric: boolean } | null {
  const field = findNativeRubricField();
  if (!field) return null;
  return { anchor: field, replaceNativeRubric: true };
}

function triggerLabel(selected: Set<RubricBandId>): string {
  if (selected.size === ALL_IDS.length) return "All bands";
  if (selected.size === 1) {
    const id = [...selected][0];
    return RUBRIC_BAND_OPTIONS.find((o) => o.id === id)?.label ?? "1 selected";
  }
  return `${selected.size} selected`;
}

function bandCounts(table: HTMLTableElement): Map<RubricBandId, number> {
  const counts = new Map<RubricBandId, number>();
  for (const id of ALL_IDS) counts.set(id, 0);
  for (const row of parseScreenerRows(table)) {
    const id = classifyRubricBand(row.rubricBand);
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return counts;
}

export function applyRubricBandFilter(
  table: HTMLTableElement,
  selected: Set<RubricBandId>,
): void {
  const showAll = selected.size === ALL_IDS.length;
  for (const row of parseScreenerRows(table)) {
    const band = classifyRubricBand(row.rubricBand);
    const visible = showAll || selected.has(band);
    row.element.classList.toggle("sfe-rubric-filter-hidden", !visible);
  }
}

let selected = loadSelected();
let host: HTMLElement | null = null;
let panelOpen = false;
let nativeRubricField: HTMLElement | null = null;
let documentClickBound = false;

function refreshPanel(table: HTMLTableElement): void {
  if (!host) return;
  const counts = bandCounts(table);
  const list = host.querySelector(".sfe-rubric-filter-list");
  if (!list) return;

  list.innerHTML = "";
  for (const opt of RUBRIC_BAND_OPTIONS) {
    const label = document.createElement("label");
    label.className = `sfe-rubric-filter-option sfe-rubric-filter-option-${opt.id}`;

    const input = document.createElement("input");
    input.type = "checkbox";
    input.value = opt.id;
    input.checked = selected.has(opt.id);

    const text = document.createElement("span");
    text.className = "sfe-rubric-filter-option-label";
    text.textContent = opt.label;

    const count = document.createElement("span");
    count.className = "sfe-rubric-filter-option-count";
    count.textContent = `(${counts.get(opt.id) ?? 0})`;

    label.append(input, text, count);
    list.appendChild(label);

    input.addEventListener("change", () => {
      const next = new Set<RubricBandId>();
      list.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked').forEach((el) => {
        if (ALL_IDS.includes(el.value as RubricBandId)) next.add(el.value as RubricBandId);
      });
      selected = next.size ? next : new Set(ALL_IDS);
      saveSelected(selected);
      updateTrigger(table);
      applyRubricBandFilter(table, selected);
      refreshPanel(table);
    });
  }
}

function updateTrigger(table: HTMLTableElement): void {
  if (!host) return;
  const trigger = host.querySelector<HTMLButtonElement>(".sfe-rubric-filter-trigger");
  if (trigger) trigger.textContent = triggerLabel(selected);
  host.classList.toggle("sfe-rubric-filter-active", selected.size !== ALL_IDS.length);
  applyRubricBandFilter(table, selected);
}

function closePanel(): void {
  panelOpen = false;
  host?.classList.remove("sfe-rubric-filter-open");
  host?.querySelector(".sfe-rubric-filter-panel")?.setAttribute("hidden", "");
}

function openPanel(table: HTMLTableElement): void {
  panelOpen = true;
  host?.classList.add("sfe-rubric-filter-open");
  host?.querySelector(".sfe-rubric-filter-panel")?.removeAttribute("hidden");
  refreshPanel(table);
}

function teardownHost(): void {
  host?.remove();
  host = null;
  nativeRubricField?.classList.remove("sfe-native-rubric-hidden");
  nativeRubricField = null;
  panelOpen = false;
}

function buildHost(table: HTMLTableElement): HTMLElement {
  const el = document.createElement("div");
  el.id = HOST_ID;
  el.className = "sfe-rubric-filter-host";
  el.innerHTML = `
    <div class="sfe-rubric-filter-field">
      <span class="sfe-rubric-filter-label">Rubric band</span>
      <div class="sfe-rubric-filter-control">
        <button type="button" class="sfe-rubric-filter-trigger" aria-haspopup="listbox" aria-expanded="false">
          ${triggerLabel(selected)}
        </button>
        <div class="sfe-rubric-filter-panel" hidden>
          <button type="button" class="sfe-rubric-filter-all">All bands</button>
          <div class="sfe-rubric-filter-list" role="listbox"></div>
        </div>
      </div>
    </div>
  `;

  el.querySelector(".sfe-rubric-filter-trigger")?.addEventListener("click", (e) => {
    e.stopPropagation();
    if (panelOpen) closePanel();
    else openPanel(table);
  });

  el.querySelector(".sfe-rubric-filter-all")?.addEventListener("click", () => {
    selected = new Set(ALL_IDS);
    saveSelected(selected);
    updateTrigger(table);
    refreshPanel(table);
  });

  if (!documentClickBound) {
    documentClickBound = true;
    document.addEventListener("click", (e) => {
      if (!panelOpen || !host) return;
      if (!host.contains(e.target as Node)) closePanel();
    });
  }

  return el;
}

function hostIsPlacedCorrectly(insert: { anchor: HTMLElement }): boolean {
  if (!host?.isConnected || !nativeRubricField) return false;
  return host.parentElement === nativeRubricField.parentElement;
}

/** Mount (or refresh) the multi-select Rubric Band filter in the screener bar. */
export function mountRubricBandFilter(table: HTMLTableElement): void {
  const insert = findFilterInsertPoint(table);
  if (!insert) {
    teardownHost();
    return;
  }

  if (host && (!host.isConnected || !hostIsPlacedCorrectly(insert))) {
    teardownHost();
  }

  if (!host) {
    host = buildHost(table);
    nativeRubricField = insert.anchor;
    insert.anchor.classList.add("sfe-native-rubric-hidden");
    insert.anchor.parentElement?.insertBefore(host, insert.anchor);
  }

  updateTrigger(table);
  if (panelOpen) refreshPanel(table);
  syncResetButtons();
}

export function syncRubricBandFilter(): void {
  const table = findScreenerTable();
  if (!table || table.id === "wheelCallsTable") {
    teardownHost();
    return;
  }
  mountRubricBandFilter(table);
}
